import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { delay, distinctUntilChanged, filter, startWith } from 'rxjs/operators';

import { ChangePasswordDialogComponent } from './change-password-dialog/change-password-dialog.component';

import { AccountService } from '../../core/auth/account.service';
import { LANGUAGES } from '../../core/language/language.constants';
import { LocalStorageService } from 'ngx-webstorage';
import { LoginService } from '../../core/login/login.service';
import { PageTitleService } from '../../shared/services/page-title.service';
import { TopbarAction, TopbarActionsService } from '../../shared/services/topbar-actions.service';
import { TranslateService } from '@ngx-translate/core';
import { FeaturesEnum } from '../../shared/constants/features.constants';
import { Authority } from '../../shared/constants/authority.constants';
import { AppConfiguration } from '../../shared/model/app-configuration.model';
import { AppConfigurationService } from '../../entities/app-configuration/app-configuration.service';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  standalone: false,
})
export class TopbarComponent implements OnInit, OnDestroy {
  FeaturesEnum = FeaturesEnum;
  languages = LANGUAGES;
  activeLanguage = 'en';
  userName = '';
  gymName = '';
  pageTitle$!: Observable<string>;
  parentTitleKey$!: Observable<string>;
  subtitle$!: Observable<string>;
  topbarActions$!: Observable<TopbarAction[]>;
  systemMessage: string = '';
  public appConfigurations: AppConfiguration[] = [];
  private parentTitleKeySubject = new BehaviorSubject<string>('');

  private routerSub?: Subscription;


  constructor(
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
    private accountService: AccountService,
    private loginService: LoginService,
    private router: Router,
    private pageTitleService: PageTitleService,
    private topbarActionsService: TopbarActionsService,
    private appConfigurationService: AppConfigurationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const langKey = this.localStorageService.retrieve('locale') || 'sr';
    this.activeLanguage = langKey;
    this.accountService.identity(true).subscribe((res: any) => {
      this.gymName = res?.companyName || '';
      this.userName = res?.firstName && res?.lastName ? res.firstName + ' ' + res.lastName : '';
    });

    this.pageTitle$ = this.pageTitleService.title$.pipe(distinctUntilChanged(), delay(0));
    this.parentTitleKey$ = this.parentTitleKeySubject.asObservable();
    this.subtitle$ = this.topbarActionsService.subtitle$.pipe(distinctUntilChanged(), delay(0));
    this.topbarActions$ = this.topbarActionsService.actions$;

    const updateParent = () => this.parentTitleKeySubject.next(this.getParentTitleKey());

     this.appConfigurationService
          .getByNameWithoutCompany('systemMessage')
          .subscribe((appConf) => {
            this.systemMessage = this.getSystemMessage(appConf.body!);
      });

    this.routerSub = this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((e) => e instanceof NavigationEnd)
      )
      .subscribe(() => updateParent());
  }

  getSystemMessage(rawMessage: string): string {
    if (!rawMessage) return '';

    const parts = rawMessage.split(';');
    if (parts.length < 3) return '';

    const startStr = parts[0].trim();
    const endStr = parts[1].trim();
    const message = parts.slice(2).join(';').trim();

    const start = new Date(startStr);
    const end = new Date(endStr);
    const now = new Date();

    if (now >= start && now <= end) {
      return message;
    }

    return '';
  }


  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  changeLanguage(languageKey: string): void {
    this.activeLanguage = languageKey;
    this.translateService.use(languageKey);
    this.localStorageService.store('locale', languageKey);
  }

  openChangePassword(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '460px',
      maxWidth: '95vw',
      autoFocus: 'input',
      panelClass: ['modal-above-topbar', 'dialog-container', 'cp-dialog-panel'],
    });
  }

  logout(): void {
    this.loginService.logout();
    this.router.navigate(['/account/login']);
  }

  runAction(action: TopbarAction): void {
    this.topbarActionsService.run(action);
  }

  goHome(): void {
    try {
      // Align behavior with FeatureRouteAccessService: admin bypass
      const isAdmin = this.accountService.hasAnyAuthority([Authority.ADMIN]);
      const allowed = isAdmin || this.accountService.canAccessFeature(FeaturesEnum.HOME);
      if (allowed) {
        this.router.navigate(['/home']);
      } else {
        this.router.navigate(['accessdenied']);
      }
    } catch {
      this.router.navigate(['accessdenied']);
    }
  }

  private getParentTitleKey(): string {
    try {
      const url = (this.router.url || '').split('?')[0];
      const segment = (url.split('/').filter(Boolean)[0] || '').toLowerCase();
      if (segment === 'booking') return 'bookingApp.global.menu.booking';
      if (segment === 'ids') return 'idsApp.global.menu.idsUrl';
      if (segment === 'reports') return 'global.menu.reports';
      return '';
    } catch {
      return '';
    }
  }
}
